/**
 * Concrete `StepType` renderers shipped by `@nextday/survey-kit`.
 *
 * Each renderer is a `StepRenderer<TData>` whose `dataSchema` validates the
 * JSON `data` payload before the runtime forwards typed props. To assemble
 * a working registry, pass an array of these into `defineRegistry()`.
 *
 * Group 1 (NX-9301): info_screen · single_choice · multi_choice · scenario_picker.
 * Subsequent renderer groups (NX-9302..NX-9304) extend this barrel.
 */

import accountCreateRenderer from "@survey-kit/renderers/account_create";
import dualChoiceHeroRenderer from "@survey-kit/renderers/dual_choice_hero";
import overlayPromptRenderer from "@survey-kit/renderers/overlay_prompt";
import characterRevealGateRenderer from "@survey-kit/renderers/character_reveal_gate";
import firstMessagePreviewRenderer from "@survey-kit/renderers/first_message_preview";
import handoffConfigRenderer from "@survey-kit/renderers/handoff_config";
import infoScreenRenderer from "@survey-kit/renderers/info_screen";
import loadingScreenRenderer from "@survey-kit/renderers/loading_screen";
import matchRevealRenderer from "@survey-kit/renderers/match_reveal";
import multiChoiceRenderer from "@survey-kit/renderers/multi_choice";
import nameCaptureRenderer from "@survey-kit/renderers/name_capture";
import paywallHandoffRenderer from "@survey-kit/renderers/paywall_handoff";
import revealGateRenderer from "@survey-kit/renderers/reveal_gate";
import scenarioPickerRenderer from "@survey-kit/renderers/scenario_picker";
import singleChoiceRenderer from "@survey-kit/renderers/single_choice";
import sliderRenderer from "@survey-kit/renderers/slider";
import textShortRenderer from "@survey-kit/renderers/text_short";
import trustScreenRenderer from "@survey-kit/renderers/trust_screen";
import userSyncRenderer from "@survey-kit/renderers/user_sync";
import validationScreenRenderer from "@survey-kit/renderers/validation_screen";
import valueMapRenderer from "@survey-kit/renderers/value_map";
import { defineRegistry } from "@survey-kit/registry";
import type { StepRenderer, StepRendererRegistry } from "@survey-kit/types";

export {
  infoScreenRenderer,
  singleChoiceRenderer,
  multiChoiceRenderer,
  scenarioPickerRenderer,
  sliderRenderer,
  nameCaptureRenderer,
  textShortRenderer,
  validationScreenRenderer,
  trustScreenRenderer,
  loadingScreenRenderer,
  accountCreateRenderer,
  characterRevealGateRenderer,
  firstMessagePreviewRenderer,
  handoffConfigRenderer,
  paywallHandoffRenderer,
  userSyncRenderer,
  revealGateRenderer,
  matchRevealRenderer,
  valueMapRenderer,
  dualChoiceHeroRenderer,
  overlayPromptRenderer,
};

export { infoScreenSchema, type InfoScreenData } from "@survey-kit/renderers/info_screen";
export { singleChoiceSchema, type SingleChoiceData } from "@survey-kit/renderers/single_choice";
export { multiChoiceSchema, type MultiChoiceData } from "@survey-kit/renderers/multi_choice";
export {
  scenarioPickerSchema,
  type ScenarioPickerData,
} from "@survey-kit/renderers/scenario_picker";
export {
  sliderSchema,
  type SliderData,
  type SliderSingleData,
  type SliderGroupData,
} from "@survey-kit/renderers/slider";
export { nameCaptureSchema, type NameCaptureData } from "@survey-kit/renderers/name_capture";
export { textShortSchema, type TextShortData } from "@survey-kit/renderers/text_short";
export {
  validationScreenSchema,
  type ValidationScreenData,
} from "@survey-kit/renderers/validation_screen";
export { trustScreenSchema, type TrustScreenData } from "@survey-kit/renderers/trust_screen";
export { loadingScreenSchema, type LoadingScreenData } from "@survey-kit/renderers/loading_screen";
export {
  accountCreateSchema,
  accountCreatePresentationSchema,
  type AccountCreateData,
  type AccountCreatePresentation,
} from "@survey-kit/renderers/account_create";
export {
  characterRevealGateSchema,
  type CharacterRevealGateData,
} from "@survey-kit/renderers/character_reveal_gate";
export {
  firstMessagePreviewSchema,
  type FirstMessagePreviewData,
} from "@survey-kit/renderers/first_message_preview";
export {
  paywallHandoffSchema,
  type PaywallHandoffData,
} from "@survey-kit/renderers/paywall_handoff";
export { userSyncSchema, type UserSyncData } from "@survey-kit/renderers/user_sync";
export {
  handoffConfigSchema,
  type HandoffConfigData,
  type HandoffResolution,
  HANDOFF_URL_ANSWER_KEY,
  resolveHandoff,
} from "@survey-kit/renderers/handoff_config";
export { revealGateSchema, type RevealGateData } from "@survey-kit/renderers/reveal_gate";
export { matchRevealSchema, type MatchRevealData } from "@survey-kit/renderers/match_reveal";
export { valueMapSchema, type ValueMapData } from "@survey-kit/renderers/value_map";
export {
  dualChoiceHeroSchema,
  type DualChoiceHeroData,
} from "@survey-kit/renderers/dual_choice_hero";
export { overlayPromptSchema, type OverlayPromptData } from "@survey-kit/renderers/overlay_prompt";

/**
 * Every renderer shipped by survey-kit — all 13 plan-required StepTypes
 * after Groups 1–4. Callers usually want `buildDefaultRegistry()`.
 */
export const allRenderers: ReadonlyArray<StepRenderer<unknown>> = [
  infoScreenRenderer,
  singleChoiceRenderer,
  multiChoiceRenderer,
  scenarioPickerRenderer,
  sliderRenderer,
  nameCaptureRenderer,
  textShortRenderer,
  validationScreenRenderer,
  trustScreenRenderer,
  loadingScreenRenderer,
  accountCreateRenderer,
  characterRevealGateRenderer,
  firstMessagePreviewRenderer,
  handoffConfigRenderer,
  paywallHandoffRenderer,
  userSyncRenderer,
  revealGateRenderer,
  matchRevealRenderer,
  valueMapRenderer,
  dualChoiceHeroRenderer,
  overlayPromptRenderer,
];

/**
 * Convenience factory: build a frozen registry containing every renderer
 * survey-kit currently ships. Callers can replace this with their own
 * subset via `defineRegistry([...])` for tests or specialized funnels.
 */
export const buildDefaultRegistry = (): StepRendererRegistry => defineRegistry(allRenderers);
